import { useEffect, useState } from "react";
import { listRequests, type ClothoRequest } from "../api";

function statusColor(s: string): string {
  switch (s) {
    case "approved":
      return "bg-emerald-100 text-emerald-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "in_review":
      return "bg-amber-100 text-amber-800";
    case "returned":
      return "bg-orange-100 text-orange-800";
    default:
      return "bg-slate-100 text-slate-700";
  }
}

function lifecycleColor(s: string): string {
  switch (s) {
    case "closed":
      return "bg-slate-100 text-slate-700";
    case "cancelled":
      return "bg-red-100 text-red-800";
    case "in_fulfillment":
      return "bg-sky-100 text-sky-800";
    case "po_approved":
      return "bg-emerald-100 text-emerald-800";
    default:
      return "bg-thread-100 text-thread-800";
  }
}

function formatCurrency(n: number | null): string {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return s;
  }
}

export default function TrackerList() {
  const [requests, setRequests] = useState<ClothoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await listRequests();
      setRequests(res.requests);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Tracker</h2>
            <p className="mt-1 text-sm text-slate-600">
              All procurement requests in flight. Replaces the manual "open
              SharePoint, sort the list, read 40 rows" routine.
            </p>
          </div>
          <button type="button" onClick={() => void load()} className="btn-secondary">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading && (
        <div className="card p-6 text-sm text-slate-500">Loading…</div>
      )}

      {!loading && requests.length === 0 && !error && (
        <div className="card p-6 text-sm text-slate-600">
          No requests yet. Go to the <strong>Intake</strong> tab, paste an email,
          extract the fields, and submit — your first request will show up here.
        </div>
      )}

      {!loading && requests.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3 font-semibold">ID</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 font-semibold">Supplier</th>
                <th className="px-4 py-3 font-semibold">Value</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 font-semibold">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requests.map((r) => (
                <tr key={r.request_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {r.request_id}
                  </td>
                  <td className="px-4 py-3 text-slate-900">
                    <div className="max-w-md truncate">{r.description}</div>
                    {r.capital_project_code && (
                      <div className="text-xs text-slate-500">
                        {r.capital_project_code}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {r.supplier_name_snapshot ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {formatCurrency(r.value_usd)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`pill ${statusColor(r.approval_status)}`}>
                      {r.approval_status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`pill ${lifecycleColor(r.lifecycle_state)}`}
                    >
                      {r.lifecycle_state}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {formatDate(r.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
